import type { FC } from 'react';
import Link from 'next/link';
import { Image } from 'components/ImageComponent';
import useLocalised from 'customHooks/useLocalised/useLocalised';
import Benefits from '../Benefits/Benefits';
import { view_hotel } from 'data/translationKeys';
import styles from './ListItemExtended.module.css';
import { Small } from 'components/text';
import useMediaQuery from '@mui/material/useMediaQuery';
import Badges from './Badges';
import type { SearchResultsByLocationType } from 'types/types';
import SimpleCarousel from 'components/simple-carousel';

interface IListItem {
  searchResults: SearchResultsByLocationType;
}

const ListItemExtended: FC<IListItem> = ({ searchResults }) => {
  const view_hotel_text = useLocalised(view_hotel);
  const hotelName = searchResults.name;
  const hotelImage = searchResults.image;
  const hotelId = searchResults.id;
  const hotelDetailsUrl = `/our-hotels/${hotelId}`;

  const hotelImages = (searchResults.images ?? []).map(img => ({
    url: typeof img === 'string' ? img : img.url,
  }));

  const showImageCarousel = hotelImages.length > 1;

  const isMobile = useMediaQuery(`(max-width:600px)`);
  const isTouchDevice = useMediaQuery('(hover: none) and (pointer: coarse)');
  const isLargeScreen = useMediaQuery('(min-width:1024px)');

  const canSwipe = isTouchDevice || isMobile || !isLargeScreen;

  return (
    <article className={styles.container}>
      <div className={styles.image}>
        <Badges searchResults={searchResults} />
        {showImageCarousel && (
          <SimpleCarousel
            images={hotelImages}
            settings={{
              enableSwipe: canSwipe,
            }}
            linkTo={hotelDetailsUrl}
          />
        )}
        {!showImageCarousel && (
          <Link href={hotelDetailsUrl} passHref>
            <a href={hotelDetailsUrl} className={styles.hotelImageLink}>
              <Image
                src={hotelImage}
                alt={`${hotelName} image`}
                style={{
                  aspectRatio: '89/50',
                  maxWidth: '100%',
                  maxHeight: '270px',
                  width: '100%',
                  height: '100%',
                }}
              />
            </a>
          </Link>
        )}
      </div>
      <div className={styles.hotelInfoContainer}>
        <div>
          <Link href={hotelDetailsUrl} passHref>
            <a href={hotelDetailsUrl} className={styles.hotelNameLink}>
              <h5>{hotelName}</h5>
            </a>
          </Link>
          <p className="extra-small">{searchResults.location}</p>
        </div>
        <div className={styles.hotelInfo}>
          <Benefits
            benefits={searchResults.benefits}
            benefitsFootnotes={searchResults.benefitsFootnotes}
          />
          <div className={styles.hotelCTA}>
            {searchResults.pre_opening && (
              <div className={styles.preOpening}>
                <p className="demi-bold">Expected opening</p>
                <p className={styles.firstOpen}>{searchResults.first_open}</p>
              </div>
            )}
            <Link href={hotelDetailsUrl} passHref>
              <a
                href={hotelDetailsUrl}
                className={`${styles.hotelButtonLink} ${styles.button}`}
              >
                {view_hotel_text}
              </a>
            </Link>
            {searchResults.benefitsFootnotes.length > 0 &&
              isMobile &&
              searchResults.benefitsFootnotes.map((footNote, index) => (
                <div key={index} className={styles.footNotesRow}>
                  <Small xs color="grey-dark">
                    {footNote}
                  </Small>
                </div>
              ))}
          </div>
        </div>
      </div>
    </article>
  );
};

export default ListItemExtended;
